import {WorklogStore} from './WorklogStore';
import {JiraClient} from './JiraClient';
import {Worklog} from './Worklog';
import {StorageService} from "./StorageService";
import {I18nService} from "./I18nService";

export interface AppSettings {
  lang: string;
  autoSync: boolean;
  jira: {
    email: string;
    token: string;
  };
}

// Sync Queue (TypeScript)
export class SyncQueue {
  private static jiraClient: JiraClient | null = null;
  private store: WorklogStore;
  private onUpdate: () => void;
  private running: boolean = false;

  constructor(
    onUpdate: () => void
  ) {
    this.store = new WorklogStore();
    this.onUpdate = onUpdate;
  }

  static getJiraClient(): JiraClient {
    if (!this.jiraClient) {
      this.jiraClient = new JiraClient();
    }
    return this.jiraClient;
  }

  async process(): Promise<void> {
    if (this.running || !navigator.onLine) {
      return;
    }

    // Ensure we operate on the latest data
    this.store.reload();

    // Capture current weekly total and freeze it until sync finishes
    const currentWeeklyTotal = this.store.weeklyTotalSec();
    WorklogStore.freezeWeeklyTotal(currentWeeklyTotal);

    const items = this.store.pendingForUpload();
    const deletions = this.store.listSorted().filter(w => w.pendingDelete && !!w.jiraId);
    if (!items.length && !deletions.length) {
      // Nothing to do; unfreeze immediately
      WorklogStore.unfreezeWeeklyTotal();
      return;
    }

    // Prepare i18n for default descriptions
    const settingsForLang = StorageService.loadSettings();
    const i18n = new I18nService();
    i18n.setLanguage((settingsForLang && settingsForLang.lang) ? settingsForLang.lang : 'en');

    this.running = true;
    try {
      // Process deletions first
      for (const w of deletions) {
        try {
          if (!w.issueKey || !w.jiraId) {
            // If jiraId is missing unexpectedly, just remove locally
            this.store.remove(w.id);
            continue;
          }
          w.status = 'uploading';
          w.error = '';
          w.updatedAt = new Date();
          this.store.update(w);

          const client = SyncQueue.getJiraClient();
          await client.deleteWorklog(w.issueKey, w.jiraId);

          // Remove locally after successful deletion
          this.store.remove(w.id);
        } catch (e: any) {
          // If offline, keep queued; if 404, treat as already deleted and remove locally
          const msg = String(e?.message || '');
          if (msg.startsWith('404')) {
            this.store.remove(w.id);
          } else {
            w.status = navigator.onLine ? 'error' : 'queued';
            w.error = e.message || 'Delete failed';
            w.updatedAt = new Date();
            this.store.update(w);
          }
        }
      }

      // Then process uploads (skip items flagged for deletion)
      for (const w of items.filter(x => !x.pendingDelete)) {
        try {
          if (!w.issueKey) {
            throw new Error('Issue key is missing');
          }
          w.status = 'uploading';
          w.error = '';
          w.updatedAt = new Date();
          this.store.update(w);

          const client = SyncQueue.getJiraClient();

          // Build description to send: if empty, use localized default "Working on {issueKey}"
          const descToSend = (w.description && w.description.trim())
            ? w.description
            : i18n.t('defaultWorklogDesc').replace('{issueKey}', w.issueKey);

          const res = await client.upsertWorklog({
            issueKey: w.issueKey,
            description: descToSend,
            start: w.start,
            durationSec: w.durationSec,
            jiraId: w.jiraId || undefined
          });

          if (res && res.id) {
            const newId = res.id;
            if (newId && newId !== w.jiraId) {
              w.jiraId = newId;
            }
          }

          // After successful sync, if original description was empty, set the default locally as well
          if (!w.description || !w.description.trim()) {
            w.description = descToSend;
          }

          w.start = res.start;
          w.end = res.end;
          // w.durationSec = res.durationSec;
          w.status = 'synced';
          w.syncedAt = new Date();
          w.updatedAt = new Date();
          this.store.update(w);
        } catch (e: any) {
          w.status = navigator.onLine ? 'error' : 'queued';
          w.error = e.message || 'Upload failed';
          w.updatedAt = new Date();
          this.store.update(w);
        }
      }
    } finally {
      // Always reset running flag and unfreeze totals; trigger a single UI update at the end
      this.running = false;
      WorklogStore.unfreezeWeeklyTotal();
      this.onUpdate();
    }
  }

  enqueueIfAuto(w: Worklog): void {
    // const s = StorageService.loadSettings();
    // if (s.autoSync) {
      if (!navigator.onLine) {
        w.status = 'queued';
        this.store.update(w);
        this.onUpdate();
      }
      this.process();
    // }
  }
}